import PageShell from "@/components/feature/PageShell";
import ScrollReveal from "@/components/feature/ScrollReveal";
import { Link } from "react-router-dom";
import { useModal } from "@/hooks/useModal";

const offerings = [
  {
    icon: "ri-shield-check-line",
    title: "Security Audits & Assessments",
    desc: "Comprehensive evaluation of your IT infrastructure against ISO 27001 and NIST frameworks. We identify vulnerabilities before attackers do.",
    items: ["ISO 27001 / NIST gap analysis", "Penetration testing & social engineering", "Cybersecurity posture audit & IR planning", "24/7 threat monitoring (SOC readiness)"],
  },
  {
    icon: "ri-bug-line",
    title: "Penetration Testing",
    desc: "Ethical hacking to expose real-world attack paths. From web apps to internal networks, we simulate what adversaries would attempt.",
    items: ["Network & infrastructure testing", "Web application testing", "Social engineering simulations", "Red team exercises"],
  },
  {
    icon: "ri-radar-line",
    title: "Threat Monitoring & SIEM",
    desc: "24/7 security operations center monitoring your environment. We detect, analyze, and respond to threats in real time.",
    items: ["Log aggregation & correlation", "Anomaly detection with AI", "Incident triage & escalation", "24/7 threat monitoring (SOC readiness)"],
  },
  {
    icon: "ri-first-aid-kit-line",
    title: "Incident Response",
    desc: "When breaches happen, speed matters. Our incident response team helps contain, eradicate, and recover from security incidents.",
    items: ["Rapid containment protocols", "Forensic investigation", "Post-incident hardening", "Cybersecurity posture audit & IR planning"],
  },
];

const certifications = ["ISO 27001", "NIST CSF", "GDPR", "SOC 2", "PCI DSS"];

export default function CybersecurityPage() {
  const { openQuote } = useModal();

  return (
    <PageShell>
      <section className="relative w-full pt-28 md:pt-36 pb-16 md:pb-24 bg-gray-50 dark:bg-dark-card">
        <div className="px-4 md:px-6 lg:px-8 mb-8">
          <Link to="/services" className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-brand-500 transition-colors">
            <i className="ri-arrow-left-line" /> Back to Services
          </Link>
        </div>
        <div className="px-4 md:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
              <span className="inline-block px-3 py-1 rounded-full bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 text-xs font-semibold uppercase tracking-wider mb-4">
                Cybersecurity
              </span>
              <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 dark:text-white mb-4">
                Protect What Matters Most
              </h1>
              <p className="text-sm md:text-base text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
                Enterprise-grade security services that keep your data, systems, and reputation safe from evolving cyber threats.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
            {offerings.map((o, i) => (
              <ScrollReveal key={o.title} delay={i + 1}>
                <div className="group p-6 md:p-8 rounded-2xl bg-white dark:bg-dark-bg border border-gray-100 dark:border-dark-border hover:border-brand-200 dark:hover:border-brand-500/30 transition-all duration-300 h-full">
                  <div className="w-12 h-12 rounded-xl bg-brand-50 dark:bg-brand-500/10 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${o.icon} text-xl text-brand-500`} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    {o.title}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
                    {o.desc}
                  </p>
                  <ul className="space-y-2">
                    {o.items.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-sm text-gray-500 dark:text-gray-400">
                        <i className="ri-check-line text-brand-500 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-24 bg-white dark:bg-dark-bg">
        <div className="px-4 md:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-10 md:mb-14 max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Frameworks & Compliance
              </h2>
              <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">
                Our engagements align with globally recognized security standards and regulatory requirements.
              </p>
            </div>
          </ScrollReveal>
          <div className="flex flex-wrap justify-center gap-3 md:gap-4 max-w-3xl mx-auto">
            {certifications.map((c) => (
              <div
                key={c}
                className="px-5 py-2.5 rounded-full bg-gray-50 dark:bg-dark-card border border-gray-200 dark:border-dark-border text-sm font-semibold text-gray-700 dark:text-gray-300"
              >
                {c}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-24 bg-brand-500 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 px-4 md:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Is Your Organisation Secure?
          </h2>
          <p className="text-sm md:text-base text-brand-100 max-w-lg mx-auto mb-8">
            Get a free security assessment and discover your risk exposure.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={openQuote}
              className="px-8 py-3.5 rounded-full bg-white text-brand-600 text-sm font-semibold hover:bg-brand-50 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
            >
              Get a Security Audit
            </button>
            <Link
              to="/services"
              className="px-8 py-3.5 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-semibold hover:bg-white/20 transition-all border border-white/20 whitespace-nowrap text-center cursor-pointer"
            >
              All Services
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}